import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Server-side writers for the sports cache tables. All writes go through the
 * service-role client so RLS (which only allows admins to INSERT/UPDATE the
 * cache) doesn't silently reject them from browser callers.
 *
 * The browser is read-only against sports_stats_cache / sports_matches_cache /
 * sports_h2h_cache; it invokes these server functions to persist provider
 * payloads.
 */

const TableEnum = z.enum([
  "sports_stats_cache",
  "sports_matches_cache",
  "sports_h2h_cache",
]);

const CacheWriteInput = z.object({
  table: TableEnum,
  row: z.record(z.string(), z.unknown()),
  ttlMin: z.number().int().positive().max(60 * 24 * 7),
});

function conflictColsFor(table: z.infer<typeof TableEnum>): string {
  if (table === "sports_matches_cache") return "match_id,source";
  if (table === "sports_h2h_cache") {
    return "home_team_id,away_team_id,competition_id,source";
  }
  return "scope,scope_id,source";
}

export const writeSportsCache = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => CacheWriteInput.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const nowIso = new Date().toISOString();
    const expiresIso = new Date(Date.now() + data.ttlMin * 60_000).toISOString();
    const payload = {
      ...data.row,
      fetched_at: nowIso,
      expires_at: expiresIso,
    };
    const { error } = await supabaseAdmin
      .from(data.table)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .upsert(payload as any, { onConflict: conflictColsFor(data.table) });
    if (error) {
      console.error("[sportsCache.write] upsert failed", {
        table: data.table,
        message: error.message,
        code: error.code,
      });
      return { ok: false as const, error: error.message };
    }
    return { ok: true as const };
  });

const HealthInput = z.object({
  provider: z.string().min(1).max(64),
  success: z.boolean(),
  error: z.string().max(500).optional().nullable(),
});

export const writeSportsProviderHealth = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => HealthInput.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: existing, error: readErr } = await supabaseAdmin
      .from("sports_provider_health")
      .select("success_count,error_count")
      .eq("provider", data.provider)
      .maybeSingle();
    if (readErr) {
      console.error("[sportsProviderHealth.read] failed", {
        provider: data.provider,
        message: readErr.message,
      });
    }
    const sc = (existing?.success_count ?? 0) + (data.success ? 1 : 0);
    const ec = (existing?.error_count ?? 0) + (data.success ? 0 : 1);
    const nowIso = new Date().toISOString();
    const { error } = await supabaseAdmin
      .from("sports_provider_health")
      .upsert(
        {
          provider: data.provider,
          status: data.success ? "ok" : "degraded",
          last_synced_at: data.success ? nowIso : undefined,
          last_error_at: data.success ? undefined : nowIso,
          last_error: data.success ? null : data.error ?? "unknown",
          success_count: sc,
          error_count: ec,
          updated_at: nowIso,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        { onConflict: "provider" },
      );
    if (error) {
      console.error("[sportsProviderHealth.write] upsert failed", {
        provider: data.provider,
        message: error.message,
        code: error.code,
      });
      return { ok: false as const, error: error.message };
    }
    return { ok: true as const };
  });